import { customElement } from "solid-element";
import { Accessor, Component, createRenderEffect, createSignal, Setter, Signal } from "solid-js";
import { render } from 'solid-js/web'
//import ko from 'knockout'
import './index.css';
import '@lib/draggable';
import '@lib/tooltip';
import '@lib/visibleWith';
import './Common/lib/contextmenu';
import { StoryDefinition, StoryMetadata } from "@lib/stories";

// onMouseWheel was removed from typescript (moved to window).
declare module 'solid-js' {
    namespace JSX {
        interface IntrinsicElements {
            div: HTMLAttributes<HTMLDivElement> & {
                onMouseWheel?: (event: MouseEvent) => void;
            };
        }
    }
}

declare global {
    interface ST {
        OpenOverlay(element: HTMLElement | undefined, xmlRequest: any): Promise<void>
        OpenViewInDialog(viewID: string, identity: string): Promise<void>
        TransformImagePath(path: string | undefined): string
    }

    interface ComponentDefinition {
        name: string,
        component: Component<any>,
        defaultParams: any,
        module: string,
        metadata: () => StoryMetadata<Component>
        stories: Record<string, () => StoryDefinition<Component>>,
    }

    interface Window {
        ST: ST
        solidComponents: Record<string, ComponentDefinition>
    }
}

export async function registerSolidJSComponents(components: Record<string, () => Promise<unknown>>) {
    const loadedComponents = await Promise.allSettled(Object.entries(components).map(async (cmp) => {
        const fpath: string = cmp[0];
        const module: any = await cmp[1]();

        if (fpath.endsWith('.stories.tsx')) return;
        if (fpath.endsWith('.test.tsx')) return;

        const detail = componentDetails(fpath);
        if (detail && module.default) {
            window.solidComponents = window.solidComponents || { };

            window.solidComponents[detail.componentName] = {
                name: detail.componentName,
                component: module.default,
                module: import.meta.env.VITE_NS,
                defaultParams: module.defaultParams ?? { },
                stories: { },
                metadata: () => ({
                    component: module.default,
                    props: { }
                })
            }

            const storiesModule = await components[fpath.replace('.tsx', '.stories.tsx')]?.() as any

            if (storiesModule) {
                const { default: metadata, ...stories } = storiesModule
                window.solidComponents[detail.componentName].metadata = metadata
                window.solidComponents[detail.componentName].stories = stories
            }

            return detail.componentName
        }
    }))
    
    for (const loadedComponent of loadedComponents) {
        if (loadedComponent.status === 'rejected' || !loadedComponent.value) {
            continue;
        }

        const componentName = loadedComponent.value
        const { component, defaultParams } = window.solidComponents[componentName]

        if (customElements.get(componentName) || (window as any).ko?.components.isRegistered(componentName)) {
            try { console.error(`Duplicate Component: ${componentName}`); }
            catch { }
        } else {
            //console.log(`Registering SolidJS Component: ${componentName}`);
            customElement(componentName, defaultParams, component);
        }
    }

    //window.dispatchEvent(solidJSCreated);
}

export async function registerKnockoutComponents(modules: Record<string, () => Promise<unknown>>) {
    return;
}

function componentDetails(fpath: string) {
    const idx = fpath.indexOf('/components/') + '/components/'.length;
    const parts = fpath.substring(idx).split('/');

    const res = {
        componentName: parts[parts.length - 1].split('.').slice(0, -1).join('.'),
        dirName: parts[parts.length - 2],
    }; 

    if ((res.dirName === res.componentName) || !res.dirName) {
        return res;
    }
}

/**
 * Ensure global styles associated with components are cascaded down through
 * each shadowroot node.
 */
export function initCascadingStyleSheets()
{
    let styles: any = null;

    const styleObserver = new MutationObserver((mutations) => {
        for (var m of mutations) {
            for (var n of m.addedNodes) {
                const key = n.parentElement?.getAttribute('data-vite-dev-id');
                if (key) {
                    const sheet = styles[key];
                    sheet.replace(n.textContent);
                }
            }
        }
    });

    const ignoredStylesheets: string[] = ['bootstrap'];
    function initStylesheets() {
        if (!styles) {
            // We need to ensure only component styles are cascaded down. Blindly cascading all sheets causes issues
            // when there are relative paths to resources like fonts.
            styles = styles || Array.from(document.head.querySelectorAll('style,link'))
                .reduce((obj: any, it) => {
                    if (it.nodeName === 'STYLE' && it.getAttribute('data-vite-dev-id')) {
                        const key = it.getAttribute('data-vite-dev-id') || '';
                        const stylesheet = cloneStylesheet(it);

                        if (stylesheet) {
                            obj[key] = stylesheet;

                            // Hot-swap support
                            styleObserver.observe(it, {
                                childList: true
                            });
                        }
                    }
                    else if (it.nodeName === 'LINK' && it.getAttribute('rel') === 'stylesheet' && it.getAttribute('href')) {
                        const key = (it.getAttribute('href') || '').toLowerCase();

                        const shouldInclude = it.getAttribute('data-always-include') || !ignoredStylesheets.find(s => key.indexOf(s) >= 0)
                        if (shouldInclude) {
                            const stylesheet = cloneStylesheet(it);
                            if (stylesheet) {
                                obj[key] = stylesheet;
                            }
                        }

                        // We only need to apply bundle styles to components, so detach this from the main from
                        if (key.endsWith('.bundle.js')) {
                            it.parentNode?.removeChild(it);
                        }
                    }

                    return obj;
                }, {});
        }
    }

    function loadBaseUrl(href: string) {
        let res = undefined;

        const idx = href?.lastIndexOf('/') || -1;
        if (idx > -1) {
            res = href.substring(0, idx);
        }

        return res;
    }

    function cloneStylesheet(lnk: any) {
        const sheet = lnk.sheet;
        const baseURL = loadBaseUrl(lnk.getAttribute('href'));

        if (!sheet) {
            return;
        }

        const stylesheet = new CSSStyleSheet({
            baseURL
        });

        const cssText = Array.from(sheet.cssRules)
            .map((rule: any) => rule.cssText)
            .join('\n');

        stylesheet.replaceSync(cssText);
        return stylesheet;
    }

    // Detect the addition of components and add references to the global stylesheets into them
    HTMLElement.prototype.attachShadow = (function (attachShadow: any) {
        return function (this: any) {
            initStylesheets();

            const sh = attachShadow.apply(this, arguments)
            for (var key of Object.keys(styles)) {
                const css = styles[key];
                sh.adoptedStyleSheets.push(css);
            }

            return sh
        }
    })(HTMLElement.prototype.attachShadow);
}

declare global {
    interface Window {
        SolidJs: {
            renderSolidComponent: (component: Component, element: HTMLElement, props: any) => void,
            createSignal: typeof createSignal
        }
    }
}
window.SolidJs = {
    createSignal: createSignal,
    renderSolidComponent: (component, element, props) => {
        render(() => component(props ?? {}), element)
    }
};

// Prepare the binding directive
declare module 'solid-js' {
    namespace JSX {
        interface Directives {
            model: [accessor: Accessor<string | boolean>, setter: Setter<string | boolean>]
        }
    }
}

declare global {
    interface Window {
        model: (accessor : Accessor<string | boolean>, setter: Setter<string | boolean>) => void;

    }
}

window.model = window.model ?? ((el: HTMLInputElement, accessor: () => Signal<string | boolean>) => {
    const [field, setField] = accessor() || [];

    if (!field || !setField) return;

    el.addEventListener("input", onInput);

    createRenderEffect(() => (el.type === 'checkbox' ? el.checked = field() as boolean : el.value = field() as string));

    function onInput(this: HTMLInputElement, e: Event) {
        if (this.type === 'checkbox') {
            setField(this.checked)
        } else {
            setField(this.value);
        }
    }
});
